import {
  Theme,
  TextLink,
  Button,
  ButtonStyles,
  ButtonSize,
  TextLinkUnderlineStyle,
  NextgenImage,
  Modal,
  useTranslation,
  useSession,
  useBreakpoint,
  Breakpoints,
} from '@nextgen-web-framework/all';
import { useState, useEffect } from 'react';
import { getTimeDifference, CountDownInterface } from '../../utils/countDownUtil';
import styles from './ImpersonationBanner.module.scss';
import { ImpersonationBannerProps } from './types/propsTypes';

const ImpersonationBanner = (props: ImpersonationBannerProps) => {
  const config = ConfigService.getConfigs();
  const ASSETS_BASE_PATH = config.NEXT_ASSETS_BASE_PATH;
  const t = useTranslation();
  const { session } = useSession();
  const viewPortType = useBreakpoint() as Breakpoints;
  const isDesktop = [Breakpoints.XL, Breakpoints.LG].includes(viewPortType);
  const isMobile = [Breakpoints.XS, Breakpoints.SM].includes(viewPortType);
  const endTime = session.act?.expiryTime; //TODO: change to let, allow reassign endtime when session extended.
  const [showSessionReset, setShowSessionReset] = useState(false);
  const [showSessionExtendModal, setShowSessionExtendModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [isSessionExtendModalClosed, setIsSessionExtendModalClosed] = useState(false);

  const [countdown, setCountdown] = useState<CountDownInterface>(getTimeDifference(endTime ?? ''));

  const extendSessionHandler = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (props.extendSessionCallback && typeof props.extendSessionCallback === 'function') {
      props.extendSessionCallback(e);
    }
  };

  const onSessionExtendModalClose = () => {
    setShowSessionExtendModal(false);
    setIsSessionExtendModalClosed(true);
  };

  const formatCountDownValue = (value: number) => String(value).padStart(2, '0');

  useEffect(() => {
    if (endTime) {
      const extend = props.timeoutAlertInSeconds ?? 0;
      let timer = getTimeDifference(endTime ?? '');
      setShowSessionReset(false);
      setShowSessionExtendModal(false);
      setShowSessionExpiredModal(false);
      setIsSessionExtendModalClosed(false);

      const interval = setInterval(() => {
        timer = getTimeDifference(endTime ?? '');
        setCountdown(timer);
        const hoursIntoSeconds = timer.hours * 60 * 60;
        const minutesIntoSeconds = timer.minutes * 60;
        const totalSeconds = hoursIntoSeconds + minutesIntoSeconds + timer.seconds;
        if (totalSeconds <= 0) {
          setShowSessionReset(false);
          setShowSessionExtendModal(false);
          setShowSessionExpiredModal(true);
        } else if (totalSeconds <= extend) {
          setShowSessionReset(true);
          setShowSessionExpiredModal(false);
          setShowSessionExtendModal(true);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [endTime]);

  const renderBanner = (): React.ReactNode => {
    if (endTime) {
      return (
        <div
          className={styles['impersonation-container']}
          data-testid="impersonation-container-testid"
          id="impersonation-banner-id"
        >
          <div className={styles['impersonation-left-wrapper']}>
            {!isMobile && (
              <div className={styles['impersonation-left-content']}>
                <div className={styles['impersonation-agent']}>
                  <div className={styles['impersonation-agent-icon']}>
                    <NextgenImage
                      src={`${ASSETS_BASE_PATH}/images/Agent.svg`}
                      alt={''}
                      className={styles['impersonation-agent-icon-img']}
                    />
                  </div>
                  <div>{props.actorName}</div>
                </div>
                {isDesktop && (
                  <div className={styles['impersonation-stroke']}>
                    <NextgenImage
                      src={`${ASSETS_BASE_PATH}/images/RuleLine.svg`}
                      alt={''}
                      className={styles['impersonation-stroke-img']}
                    />
                  </div>
                )}
              </div>
            )}
            <div className={styles['impersonation-session']}>
              <div>
                {isMobile ? t('web.impersonation.session.remaining.mobile') : t('web.impersonation.session.remaining')}
              </div>
              <div className={styles['impersonation-timer']}>{`${formatCountDownValue(
                countdown?.minutes,
              )}:${formatCountDownValue(countdown?.seconds)}m`}</div>
              <div className={styles['impersonation-reset']} data-testid="impersonation-reset-testid">
                <TextLink
                  text={t('web.impersonation.reset')}
                  theme={Theme.WHITE}
                  underline={TextLinkUnderlineStyle.NOHOVER}
                  disabled={!showSessionReset}
                  onClick={extendSessionHandler}
                />
              </div>
            </div>
          </div>
          <div className={styles['impersonation-right-wrapper']}>
            {!isMobile && (
              <div className={styles['impersonation-customer']}>
                <div>{t('web.userInfo.accountNo')}</div>
                <div className={styles['impersonation-customer-data']}>{props.aboNum}</div>
              </div>
            )}
            <div className={styles['impersonation-end-button']}>
              <Button
                title={isMobile ? t('web.impersonation.endSession.mobile') : t('web.impersonation.endSession')}
                buttonStyle={ButtonStyles.SOLID}
                theme={Theme.WHITE}
                size={ButtonSize.SMALL}
                data-testid="impersonation-end-button-testid"
                onClick={props.endSessionCallback}
              />
            </div>
          </div>
        </div>
      );
    }
    return <></>;
  };

  const renderSessionExtendModal = (): React.ReactNode => {
    if (showSessionExtendModal && !isSessionExtendModalClosed && !props.isExtendedSessionClicked) {
      return (
        <div data-testid="impersonation-session-extend-testid">
          <Modal
            headline={t('web.impersonation.timeout.title')}
            primaryButtonLabel={t('web.impersonation.timeout.extend')}
            secondaryButtonLabel={t('web.impersonation.timeout.cancel')}
            onPrimaryButtonClick={(e) => {
              setShowSessionExtendModal(false);
              setIsSessionExtendModalClosed(false);
              extendSessionHandler(e);
            }}
            onSecondaryButtonClick={onSessionExtendModalClose}
            closeIcon={false}
            useReactPortal={true}
            portalContainerId="react-portal-modal"
          >
            <span>
              {t('web.impersonation.timeout.description').replace(
                /{timeoutAlertInSeconds}/g,
                String(props.timeoutAlertInSeconds ?? 0),
              )}
            </span>
          </Modal>
        </div>
      );
    }
    return <></>;
  };

  const renderSessionExpiredModal = (): React.ReactNode => {
    if (showSessionExpiredModal) {
      return (
        <div data-testid="impersonation-session-expired-testid">
          <Modal
            headline={t('web.impersonation.session.expired')}
            primaryButtonLabel={t('web.impersonation.session.ok')}
            onPrimaryButtonClick={props.endSessionCallback}
            closeIcon={false}
            data-testid="impersonation-session-expired-testid"
            useReactPortal={true}
            portalContainerId="react-portal-modal"
          />
        </div>
      );
    }
    return <></>;
  };

  return (
    <>
      {renderBanner()}
      {renderSessionExtendModal()}
      {renderSessionExpiredModal()}
    </>
  );
};
export default ImpersonationBanner;
